/**
 * PRD.227 — Add a billing adjustment (Debit or Credit).
 *
 * Clinic-facing semantics:
 *   - Debit  → adds to patient balance
 *   - Credit → reduces patient balance
 *
 * V1 fields: type · amount · reason · note · optional invoice link.
 * Payments stay separate from adjustments (PRD §5.3); this dialog
 * never touches `invoice_payments`. The shared patient billing
 * aggregate folds in debit/credit automatically via SQL.
 */

import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import {
  qInvoicesByPatient,
  CREDIT_REASONS,
  DEBIT_REASONS,
  type AdjustmentType,
  type InvoiceRow,
} from '@helios/data-access';
import { isPositive, parseAmountInput, toCents } from '@helios/domain';
import { useFormatMoney, useRegionalSettings } from '../../hooks/useFormatMoney';

// ─── Visit Date helpers (PRD.229) ───

function todayISODate(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function visitDateToIso(date: string): string {
  const [y, m, d] = date.split('-').map((s) => parseInt(s, 10));
  if (!y || !m || !d) return new Date().toISOString();
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  orgId: string;
  /**
   * When set, the dialog opens pre-scoped to a specific invoice (the
   * invoice picker becomes "Linked invoice" with this id selected and
   * disabled). Used from invoice detail surfaces.
   */
  prefilledInvoiceId?: string | null;
  onRecorded?: () => void;
}

export function AddAdjustmentDialog({
  open,
  onOpenChange,
  patientId,
  orgId,
  prefilledInvoiceId,
  onRecorded,
}: Props) {
  const { db } = usePowerSyncDb();
  const { user } = useAuth();
  const fmt = useFormatMoney();
  const regional = useRegionalSettings();
  const currencySymbol = regional.money_format.currency_symbol;

  // For the optional invoice picker. Only loaded when the dialog is
  // open to avoid the cost on idle patient pages.
  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(
    open && patientId
      ? qInvoicesByPatient(patientId)
      : {
          key: ['noop-patient-invoices'] as const,
          sql: 'SELECT NULL WHERE 0',
          params: [],
          watch: [],
        },
  );

  const [type, setType] = useState<AdjustmentType>('credit');
  const [amountInput, setAmountInput] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [invoiceId, setInvoiceId] = useState<string>('');
  // PRD.229 — Visit Date anchors `adjusted_at` to clinic context. Defaults today.
  const [visitDate, setVisitDate] = useState<string>(() => todayISODate());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form whenever the dialog reopens.
  useEffect(() => {
    if (!open) return;
    setType('credit');
    setAmountInput('');
    setReason('');
    setNote('');
    setInvoiceId(prefilledInvoiceId ?? '');
    setVisitDate(todayISODate());
    setSubmitting(false);
    setError(null);
  }, [open, prefilledInvoiceId]);

  const parsed = useMemo(() => parseAmountInput(amountInput), [amountInput]);
  const reasonChips = type === 'debit' ? DEBIT_REASONS : CREDIT_REASONS;
  const trimmedReason = reason.trim();

  const canSubmit =
    !!db &&
    !!patientId &&
    !submitting &&
    parsed != null &&
    isPositive(parsed) &&
    trimmedReason.length > 0;

  async function handleSubmit() {
    if (!db || !patientId || !parsed) return;
    if (!isPositive(parsed)) {
      setError('Amount must be greater than zero.');
      return;
    }
    if (!trimmedReason) {
      setError('Reason is required.');
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      // PRD.229 — adjusted_at is anchored to the user-picked Visit Date.
      const adjustedAt = visitDateToIso(visitDate);
      const cents = toCents(parsed);
      const selectedInvoiceId = invoiceId || null;
      // Best-effort encounter link: if the user picked an invoice, copy
      // its encounter_id so adjustment history aligns with that visit's
      // billing context. Pure convenience; patient-level adjustments
      // leave encounter_id NULL.
      const linkedInvoice = selectedInvoiceId
        ? invoiceRows.find((inv) => inv.id === selectedInvoiceId)
        : null;
      const encounterId = linkedInvoice?.encounter_id ?? null;

      await db.execute(
        `INSERT INTO billing_adjustments
           (id, org_id, patient_id, invoice_id, encounter_id,
            adjustment_type, amount_cents, reason, note,
            adjusted_at, created_at, updated_at, created_by, updated_by)
         VALUES (?, ?, ?, ?, ?,  ?, ?, ?, ?,  ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          orgId,
          patientId,
          selectedInvoiceId,
          encounterId,
          type,
          cents,
          trimmedReason,
          note.trim() || null,
          adjustedAt,
          now,
          now,
          user?.uid ?? null,
          user?.uid ?? null,
        ],
      );
      onRecorded?.();
      onOpenChange(false);
    } catch (e) {
      console.error('[AddAdjustmentDialog] insert failed', e);
      setError('Could not save adjustment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add Billing Adjustment"
      description="Debit adds to the patient's balance. Credit reduces it. Payments are recorded separately."
      maxWidth={480}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <Button
            variant="ghost"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button variant="gradient" size="md" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Saving…' : 'Save Adjustment'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
          padding: 'var(--sp-5) var(--sp-6)',
        }}
      >
        {/* PRD.229 — Visit Date, always shown. Defaults to today, drives adjusted_at. */}
        <Field label="Visit Date">
          <input
            type="date"
            value={visitDate}
            onChange={(e) => setVisitDate(e.target.value || todayISODate())}
            style={inputStyle()}
          />
        </Field>

        {/* Type — segmented */}
        <Field label="Type">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--sp-2)',
              padding: 3,
              background: 'var(--bg-surface-2)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <SegmentedButton
              active={type === 'debit'}
              onClick={() => {
                setType('debit');
                setReason('');
              }}
              tone="warning"
              title="Debit"
              subtitle="adds to balance"
            />
            <SegmentedButton
              active={type === 'credit'}
              onClick={() => {
                setType('credit');
                setReason('');
              }}
              tone="success"
              title="Credit"
              subtitle="reduces balance"
            />
          </div>
        </Field>

        {/* Amount */}
        <Field label="Amount">
          <div style={{ position: 'relative' }}>
            <span
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
                pointerEvents: 'none',
              }}
            >
              {currencySymbol}
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={amountInput}
              onChange={(e) => {
                setAmountInput(e.target.value);
                if (error) setError(null);
              }}
              placeholder="0.00"
              style={inputStyle(24)}
              autoFocus
            />
          </div>
          {parsed && isPositive(parsed) && (
            <HintRow>
              <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
                {type === 'debit' ? 'Adds ' : 'Reduces '}
                <strong style={{ color: 'var(--text)' }}>{fmt(parsed)}</strong>
                {type === 'debit' ? ' to patient balance' : ' from patient balance'}
              </span>
            </HintRow>
          )}
        </Field>

        {/* Reason */}
        <Field label="Reason">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={type === 'debit' ? 'e.g. Missed charge' : 'e.g. Returned item'}
            style={inputStyle()}
          />
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginTop: 6,
            }}
          >
            {reasonChips.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => setReason(suggestion)}
                style={chipStyle(reason === suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </Field>

        {/* Optional invoice link */}
        <Field label="Linked invoice (optional)">
          <select
            value={invoiceId}
            onChange={(e) => setInvoiceId(e.target.value)}
            disabled={!!prefilledInvoiceId}
            style={inputStyle()}
          >
            <option value="">— Patient-level (no invoice link) —</option>
            {invoiceRows.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {(inv.invoice_no ?? inv.id.slice(0, 8)) +
                  (inv.date_iso ? ` · ${inv.date_iso}` : '') +
                  (inv.status ? ` · ${inv.status}` : '')}
              </option>
            ))}
          </select>
          <HintRow>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
              Linking is optional — the patient balance reflects the adjustment either way.
            </span>
          </HintRow>
        </Field>

        {/* Note */}
        <Field label="Note (optional)">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Returned 2x Amoxicillin per Dr. Cruz"
            style={inputStyle()}
          />
        </Field>

        {error && (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-danger-text, #b91c1c)',
              background: 'var(--color-danger-bg, #fef2f2)',
              border: '1px solid var(--color-danger-border, #fecaca)',
              padding: 'var(--sp-2) var(--sp-3)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Small layout helpers ───

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500, color: 'var(--text-muted)' }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function HintRow({ children }: { children: React.ReactNode }) {
  return <div style={{ marginTop: 4 }}>{children}</div>;
}

function SegmentedButton({
  active,
  onClick,
  tone,
  title,
  subtitle,
}: {
  active: boolean;
  onClick: () => void;
  tone: 'warning' | 'success';
  title: string;
  subtitle: string;
}) {
  const activeBg = tone === 'warning' ? 'var(--color-warning-bg)' : 'var(--color-success-bg)';
  const activeFg = tone === 'warning' ? 'var(--color-warning-text)' : 'var(--color-success-text)';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? activeBg : 'transparent',
        color: active ? activeFg : 'var(--text-muted)',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        padding: '10px 12px',
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        fontWeight: 600,
      }}
    >
      <span style={{ fontSize: 'var(--font-size-sm)' }}>{title}</span>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 500,
          opacity: active ? 0.85 : 1,
        }}
      >
        {subtitle}
      </span>
    </button>
  );
}

function inputStyle(paddingLeft = 12): React.CSSProperties {
  return {
    width: '100%',
    height: 36,
    padding: `0 12px 0 ${paddingLeft}px`,
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    color: 'var(--text)',
    fontSize: 'var(--font-size-sm)',
  };
}

function chipStyle(active: boolean): React.CSSProperties {
  return {
    height: 26,
    padding: '0 10px',
    borderRadius: 'var(--radius-full)',
    border: active ? '1px solid var(--color-primary)' : '1px solid var(--border)',
    background: active ? 'var(--color-primary-bg, #eef2ff)' : 'var(--bg-surface)',
    color: active ? 'var(--color-primary, #4f46e5)' : 'var(--text-muted)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 500,
    cursor: 'pointer',
  };
}
